import type { TokenView } from './types'

/**
 * 응답 **헤더**에서 토큰 4종을 꺼낸다 (설계 T1).
 *
 * `Authorization` / `X-Refresh-Token` / `X-Access-Token-Expires-At` / `X-Refresh-Token-Expires-At`
 * 는 facecock-was `CorsConfig` 의 `exposedHeaders` 에 이미 등록돼 있어 브라우저에서 읽을 수 있다.
 *
 * axios 는 헤더명을 소문자로 정규화하지만, `fetch`·테스트 더블·프록시를 거치면 대소문자가
 * 섞일 수 있어 **대소문자 무관**으로 조회한다.
 */
export type RawHeaders = Record<string, unknown> | { get?: (name: string) => unknown }

function readHeader(headers: RawHeaders | undefined, name: string): string | null {
  if (!headers) return null

  // AxiosHeaders / Headers 처럼 get() 을 가진 객체
  const getter = (headers as { get?: (n: string) => unknown }).get
  if (typeof getter === 'function') {
    const viaGetter = getter.call(headers, name)
    if (typeof viaGetter === 'string' && viaGetter.length > 0) return viaGetter
  }

  const lower = name.toLowerCase()
  for (const [key, value] of Object.entries(headers as Record<string, unknown>)) {
    if (key.toLowerCase() === lower && typeof value === 'string' && value.length > 0) {
      return value
    }
  }
  return null
}

function stripBearer(value: string): string {
  return value.replace(/^Bearer\s+/i, '').trim()
}

/**
 * @returns 4종이 모두 있으면 `TokenView`, 하나라도 없으면 `null`.
 *          `null` 은 호출부가 로그인/갱신 실패로 처리한다(fail-closed).
 */
export function readTokenHeaders(headers: RawHeaders | undefined): TokenView | null {
  const rawAuth = readHeader(headers, 'Authorization')
  const refreshToken = readHeader(headers, 'X-Refresh-Token')
  const accessTokenExpiresAt = readHeader(headers, 'X-Access-Token-Expires-At')
  const refreshTokenExpiresAt = readHeader(headers, 'X-Refresh-Token-Expires-At')

  if (!rawAuth || !refreshToken || !accessTokenExpiresAt || !refreshTokenExpiresAt) return null

  const accessToken = stripBearer(rawAuth)
  if (!accessToken) return null

  return { accessToken, refreshToken, accessTokenExpiresAt, refreshTokenExpiresAt }
}
